// Frontend wrapper around the shared progress helpers. Adapts the web's
// Question / Submission types into the structural ProgressQuestion /
// ProgressSubmission shapes expected by the shared module.

import {
  computeProgress as computeProgressShared,
  isQuestionComplete,
  matchesCondition as matchesConditionShared,
  type CompletionReason,
  type ProgressQuestion,
  type ProgressResult as SharedProgressResult,
  type ProgressSubmission,
} from '@l2u/shared/progress';
import type { Question, Submission } from './types.ts';

export type { CompletionReason };

function toProgressQuestion(q: Question): ProgressQuestion {
  return {
    id: q.id,
    type: q.type,
    required: q.required,
    requires_upload: q.requires_upload,
    parent_id: q.parent_id,
    condition: q.condition as { value: unknown } | null,
    adoptable_doc_id: q.adoptable_doc_id,
    adoption_triggers: q.adoption_triggers.map((t) => ({
      trigger_value: t.trigger_value,
      document_id: t.document_id,
    })),
  };
}

function toProgressSubmission(s: Submission): ProgressSubmission {
  return {
    question_id: s.question_id,
    answer: s.answer,
    submission_files: s.submission_files.map((f) => ({
      id: f.id,
      option_key: f.option_key,
    })),
    submission_adoptions: s.submission_adoptions.map((a) => ({
      document_id: a.document_id,
      option_key: a.option_key,
    })),
  };
}

export function matchesCondition(answer: unknown, expected: unknown): boolean {
  return matchesConditionShared(answer, expected);
}

export type ProgressBreakdown = {
  progress: number;
  totalRequired: number;
  completedRequired: number;
  visibleIds: Set<string>;
  completionByQuestion: Map<string, CompletionReason | null>;
};

export function computeProgress(
  questions: Question[],
  submissions: Submission[],
): ProgressBreakdown {
  const pq = questions.map(toProgressQuestion);
  const ps = submissions.map(toProgressSubmission);
  const result: SharedProgressResult = computeProgressShared(pq, ps);

  const subByQ = new Map<string, Submission>();
  for (const s of submissions) subByQ.set(s.question_id, s);

  const completionByQuestion = new Map<string, CompletionReason | null>();
  for (const q of questions) {
    if (!result.visibleIds.has(q.id)) continue;
    completionByQuestion.set(
      q.id,
      isQuestionComplete(toProgressQuestion(q), subByQ.get(q.id) ? toProgressSubmission(subByQ.get(q.id)!) : undefined),
    );
  }

  return {
    progress: result.progress,
    totalRequired: result.totalRequired,
    completedRequired: result.completedRequired,
    visibleIds: result.visibleIds,
    completionByQuestion,
  };
}

export function questionCompletion(
  q: Question,
  sub: Submission | undefined,
): CompletionReason | null {
  return isQuestionComplete(toProgressQuestion(q), sub ? toProgressSubmission(sub) : undefined);
}

export type AdoptionTarget = {
  documentId: string;
  adoptOnly: boolean;
};

export function activeAdoptionTargets(q: Question, sub: Submission | undefined): AdoptionTarget[] {
  const targets: AdoptionTarget[] = [];
  if (
    (q.type === 'document_adopt' || q.type === 'document_upload') &&
    q.adoptable_doc_id
  ) {
    targets.push({ documentId: q.adoptable_doc_id, adoptOnly: false });
  }
  if (sub?.answer !== undefined && sub?.answer !== null) {
    for (const t of q.adoption_triggers) {
      if (matchesCondition(sub.answer, t.trigger_value)) {
        targets.push({ documentId: t.document_id, adoptOnly: t.adopt_only });
      }
    }
  }
  return targets;
}
